import React, { type ReactNode } from 'react';
import type { IOrder } from '../../_shared/interfaces/order';
import { deliveryErrorCheck, overviewErrorCheck, paymentErrorCheck } from '../../lib/helpers/helpers';

const _freeze = Object.freeze;
export interface IOrderContext {
  order: IOrder;
  stepStates: StepStates;
  updateOrder: (order: IOrder) => void;
  resetOrder: () => Promise<void>;
  isRestored: boolean;
  children?: ReactNode;
}

export enum StepState {
  Open,
  Done,
}

export interface StepStates {
  service: StepState;
  delivery: StepState;
  payment: StepState;
  overview: StepState;
}

export const defaultOrder: () => IOrder = () => ({
  manufacturer: '',
  articles: [
    {
      product: '',
      serialNumber: '',
      service: '',
      serviceIntervalMonths: 0,
      notes: '',
      amount: 1,
    },
  ],
  additionalCosts: '0',
  deliveryFromCustomer: {
    type: 'self-organized',
    price: 0,
  },
  deliveryToCustomer: {},
});

const defaultStepStates = { service: StepState.Open, delivery: StepState.Open, payment: StepState.Open, overview: StepState.Open };

export const mockOrderContext = {
  order: { ...defaultOrder() },
  stepStates: { ...defaultStepStates },
  isRestored: false,
  updateOrder: () => {},
  resetOrder: async () => {},
};
export const OrderContext = React.createContext<IOrderContext>(mockOrderContext);

class OrderContextProvider extends React.Component<
  { children?: ReactNode },
  { order: IOrder; stepStates: StepStates; isRestored: boolean }
> {
  constructor(props: Readonly<IOrderContext>) {
    super(props);
    this.state = {
      order: { ...defaultOrder() },
      stepStates: { ...defaultStepStates },
      isRestored: false,
    };
  }

  public componentDidMount(): void {
    this.restoreData();
  }

  public render() {
    return (
      <OrderContext.Provider
        value={{
          ...this.state,
          updateOrder: this.updateOrder,
          resetOrder: this.resetOrder,
        }}
      >
        {this.props.children}
      </OrderContext.Provider>
    );
  }

  public updateOrder = (order: IOrder): void => {
    const updatedOrder = {
      ...this.state.order,
      ...order,
      customer: order.customer
        ? {
            ...this.state.order.customer,
            ...order.customer,
            address: order.customer.address || this.state.order.customer?.address,
          }
        : this.state.order.customer,
    };

    // delete all articles, etc on manufacturer change
    if (
      this.state.order.manufacturer &&
      this.state.order.articles &&
      order.manufacturer &&
      order.manufacturer !== this.state.order.manufacturer
    ) {
      updatedOrder.additionalCosts = '0';
      updatedOrder.deliveryToCustomer = { ...order.deliveryToCustomer, price: 0 };
      updatedOrder.deliveryFromCustomer = { ...defaultOrder().deliveryFromCustomer };
      if (order.articles?.length) {
        updatedOrder.articles = order.articles;
      }
    }

    // set default article if no article exist
    if (updatedOrder.articles?.length === 0) {
      updatedOrder.articles = [
        {
          product: '',
          serialNumber: '',
          service: '',
          serviceIntervalMonths: 0,
          notes: '',
          additionalServices: [],
          amount: 1,
        },
      ];
    }

    this.setState(
      () => ({
        order: updatedOrder,
      }),
      () => {
        this.storeData('order', this.state.order);
        this.updateStepStates();
      },
    );
  };

  public resetOrder = (): Promise<void> => {
    return new Promise((resolve) => {
      this.setState(
        {
          order: { ...defaultOrder() },
          stepStates: { ...defaultStepStates },
        },
        () => {
          localStorage.removeItem('order');
          resolve();
        },
      );
    });
  };

  private restoreData = (): void => {
    const storedOrder = this.getStoredData('order');
    this.setState(
      {
        order: storedOrder ? storedOrder : defaultOrder(),
        isRestored: false,
      },
      () => {
        this.updateStepStates();
        this.setState({ isRestored: true });
      },
    );
  };

  private storeData = async (id: string, data: any): Promise<void> => {
    const serializeFiles = async (obj: any): Promise<any> => {
      if (obj instanceof File) {
        const base64 = await this.fileToBase64(obj);
        return {
          __isFile: true,
          name: obj.name,
          type: obj.type,
          lastModified: obj.lastModified,
          base64: base64,
        };
      }
      if (Array.isArray(obj)) {
        return Promise.all(obj.map((item) => serializeFiles(item)));
      }
      if (obj && typeof obj === 'object') {
        const serialized: any = {};
        for (const key in obj) {
          serialized[key] = await serializeFiles(obj[key]);
        }
        return serialized;
      }
      return obj;
    };

    const serializedData = await serializeFiles(data);
    localStorage.setItem(id, JSON.stringify(serializedData));
  };

  private fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  };

  private getStoredData = (id: string): IOrder | undefined => {
    const data = localStorage.getItem(id);
    if (!data) return undefined;

    const parsed = JSON.parse(data);

    const deserializeFiles = (obj: any): any => {
      if (obj && obj.__isFile) {
        const base64Data = obj.base64.split(',')[1];
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: obj.type });
        return new File([blob], obj.name, {
          type: obj.type,
          lastModified: obj.lastModified,
        });
      }
      if (Array.isArray(obj)) {
        return obj.map((item) => deserializeFiles(item));
      }
      if (obj && typeof obj === 'object') {
        const deserialized: any = {};
        for (const key in obj) {
          deserialized[key] = deserializeFiles(obj[key]);
        }
        return deserialized;
      }
      return obj;
    };

    return deserializeFiles(parsed);
  };

  private updateStepStates = () => {
    const stepStates = { ...defaultStepStates };

    if (
      this.state.order.manufacturer &&
      this.state.order.articles?.length &&
      this.state.order.articles[0].product &&
      this.state.order.articles[0].serialNumber &&
      this.state.order.articles[0].service
    ) {
      stepStates.service = StepState.Done;
    }

    const deliveryErrors = deliveryErrorCheck(this.state.order);
    if (!deliveryErrors.deliveryFromError && !deliveryErrors.deliveryToError) {
      stepStates.delivery = StepState.Done;
    } else {
      stepStates.delivery = StepState.Open;
    }

    const paymentErrors = paymentErrorCheck(this.state.order);
    stepStates.payment = paymentErrors ? StepState.Open : StepState.Done;

    const overviewErrors = overviewErrorCheck(this.state.order, stepStates, process.env.GATSBY_TAX_FIELD === 'true');
    stepStates.overview = overviewErrors ? StepState.Open : StepState.Done;

    this.setState({ stepStates });
  };
}

export default OrderContextProvider;
